import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { apiFetch } from "../api/client";
import {
  emailMessage,
  formatPhoneInput,
  isValidEmail,
  isValidPhone,
  phoneMessage,
  requiredMessage,
} from "../utils/formValidation";
import { redirectToPayPalApprove } from "../vendors/payment/paypalCheckout";

type CatalogServer = {
  id: string;
  label: string;
  description: string;
  price_label: string;
  price_amount?: string;
  instance_class?: string;
};

type OrderProvision = {
  mode?: string;
  message?: string;
  public_ip?: string;
  cname?: string;
  cname_url?: string;
  instance_id?: string;
  error?: string;
};

type ServerOrder = {
  id: string;
  status: string;
  server_type: string;
  product_label?: string;
  price_amount?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  provision?: OrderProvision | null;
  payment?: {
    provider?: string;
    error?: string;
    capture_status?: string;
  } | null;
};

type TrialResult = {
  id: string;
  message?: string;
  company_name?: string;
  email?: string;
  product_label?: string;
};

const FALLBACK_CATALOG: CatalogServer[] = [
  {
    id: "website_server",
    label: "Website Server",
    description:
      "Dedicated AWS small to host your website; $20 refundable within 5 days minus $5 fees.",
    price_label: "$20 refundable within 5 days minus $5 fees",
    price_amount: "20.00",
  },
  {
    id: "point_of_sale",
    label: "Point of Sale (POS)",
    description: "In-store POS — 15 day refundable free trial.",
    price_label: "15 day refundable free trial",
    price_amount: "20.00",
  },
  {
    id: "ecommerce_cart",
    label: "Ecommerce cart",
    description: "Online cart — 15 day refundable free trial.",
    price_label: "15 day refundable free trial",
    price_amount: "20.00",
  },
];

const SERVER_REFUND_DISCLAIMER =
  "$20 refundable within 5 days minus $5 processing fees. Cancel before provisioning completes to request a refund.";

const TRIAL_DISCLAIMER =
  "15 day refundable free trial. If you cancel within the trial window, unused fees are refundable per our terms. You will be alerted when setup is complete.";

function isWebsiteServer(id: string) {
  return id === "website_server";
}

function ReqMark() {
  return (
    <span className="req-star" aria-hidden="true">
      *
    </span>
  );
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="field-error" role="alert">
      {message}
    </p>
  );
}

type Props = {
  /** When set (e.g. on /web), hide the product dropdown and lock to that product. */
  lockedProduct?: string;
};

export function BuyServerForm({ lockedProduct }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [catalog, setCatalog] = useState<CatalogServer[]>([]);
  const [depositAmount, setDepositAmount] = useState("20.00");
  const [serverType, setServerType] = useState(lockedProduct ?? "");
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [cellPhone, setCellPhone] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyCode, setCompanyCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [order, setOrder] = useState<ServerOrder | null>(null);
  const [trial, setTrial] = useState<TrialResult | null>(null);
  const [trials, setTrials] = useState<TrialResult[]>([]);

  const showProductSelect = !lockedProduct;
  const isServer = isWebsiteServer(lockedProduct || serverType);
  const serviceOptions = (catalog.length ? catalog : FALLBACK_CATALOG).filter(
    (s) => s.id !== "website_server" && s.id !== "point_of_sale",
  );
  const selected =
    catalog.find((s) => s.id === serverType) || FALLBACK_CATALOG.find((s) => s.id === serverType);
  const amount = selected?.price_amount || depositAmount;

  useEffect(() => {
    apiFetch("/api/servers/catalog")
      .then((r) => r.json())
      .then((data) => {
        const servers = (data.servers || []) as CatalogServer[];
        setCatalog(servers.length ? servers : FALLBACK_CATALOG);
        if (data.payment?.amount) setDepositAmount(String(data.payment.amount));
        if (lockedProduct) {
          setServerType(lockedProduct);
        }
      })
      .catch(() => setCatalog(FALLBACK_CATALOG));
  }, [lockedProduct]);

  useEffect(() => {
    const payment = searchParams.get("payment");
    const orderId = searchParams.get("order");
    const reason = searchParams.get("reason");
    if (!payment) return;

    if (payment === "cancelled") {
      setError("PayPal checkout was cancelled. You can try again when ready.");
      setSearchParams({}, { replace: true });
      return;
    }

    if (payment === "error") {
      if (orderId) {
        setLoading(true);
        apiFetch(`/api/servers/orders/${encodeURIComponent(orderId)}`)
          .then((r) => r.json())
          .then((data) => {
            const payErr =
              (data?.payment && typeof data.payment.error === "string" && data.payment.error) ||
              null;
            setError(
              payErr ||
                (reason === "missing_paypal_id"
                  ? "PayPal did not return a payment id. Please try checkout again."
                  : "PayPal payment did not complete. Please try again or contact us."),
            );
          })
          .catch(() =>
            setError("PayPal payment did not complete. Please try again or contact us."),
          )
          .finally(() => {
            setLoading(false);
            setSearchParams({}, { replace: true });
          });
        return;
      }
      setError("PayPal payment did not complete. Please try again or contact us.");
      setSearchParams({}, { replace: true });
      return;
    }

    if (payment === "success" && orderId) {
      setLoading(true);
      apiFetch(`/api/servers/orders/${encodeURIComponent(orderId)}`)
        .then((r) => r.json())
        .then((data) => {
          if (!data?.id) {
            setError("Payment succeeded but we could not load your order.");
            return;
          }
          const status = String(data.status || "");
          if (status === "payment_failed" || data.payment?.error) {
            setError(
              data.payment?.error ||
                "PayPal capture failed after approval. Contact us with your order id.",
            );
            return;
          }
          setOrder(data as ServerOrder);
        })
        .catch(() =>
          setError("Payment may have succeeded — refresh or contact us with your email."),
        )
        .finally(() => {
          setLoading(false);
          setSearchParams({}, { replace: true });
        });
    }
  }, [searchParams, setSearchParams]);

  function clearField(key: string) {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function validateServerFields(): Record<string, string> {
    const errs: Record<string, string> = {};
    if (!firstName.trim()) errs.first_name = requiredMessage("First name");
    if (!lastName.trim()) errs.last_name = requiredMessage("Last name");
    if (!email.trim()) errs.email = requiredMessage("Email address");
    else if (!isValidEmail(email)) errs.email = emailMessage("Email address");
    if (!username.trim()) errs.username = requiredMessage("Username");
    else if (!isValidEmail(username)) errs.username = emailMessage("Username");
    if (cellPhone.trim() && !isValidPhone(cellPhone)) errs.cell_phone = phoneMessage("Cell phone");
    return errs;
  }

  function validateTrialFields(): Record<string, string> {
    const errs: Record<string, string> = {};
    if (showProductSelect && selectedServices.length === 0) {
      errs.server_type = requiredMessage("Service");
    }
    if (!companyName.trim()) errs.company_name = requiredMessage("Company");
    if (!firstName.trim()) errs.first_name = requiredMessage("First name");
    if (!lastName.trim()) errs.last_name = requiredMessage("Last name");
    if (!email.trim()) errs.email = requiredMessage("Email");
    else if (!isValidEmail(email)) errs.email = emailMessage();
    if (cellPhone.trim() && !isValidPhone(cellPhone)) errs.cell_phone = phoneMessage("Telephone");
    if (!companyCode.trim()) errs.company_code = requiredMessage("Company code");
    else if (!/^[a-z0-9]{4,20}$/.test(companyCode.trim())) {
      errs.company_code = "Company code must be 4–20 lowercase letters and numbers only.";
    }
    return errs;
  }

  function toggleService(id: string) {
    setSelectedServices((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
    clearField("server_type");
    setError(null);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const errs = isServer ? validateServerFields() : validateTrialFields();
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      const labels = Object.values(errs);
      setError(`Please fix ${labels.length} field${labels.length === 1 ? "" : "s"}: ${labels[0]}`);
      return;
    }

    setLoading(true);
    let redirectingToPayPal = false;
    try {
      if (isServer) {
        const res = await apiFetch("/api/servers/orders", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            server_type: serverType || lockedProduct,
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            email: email.trim(),
            username: username.trim() || email.trim(),
            cell_phone: cellPhone.trim() || undefined,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.success === false) {
          if (data.field_errors && typeof data.field_errors === "object") {
            setFieldErrors(data.field_errors as Record<string, string>);
          }
          throw new Error(
            data.detail || data.error || "We could not start PayPal checkout right now.",
          );
        }
        // Must be an absolute http(s) PayPal URL — not an in-app React route
        redirectToPayPalApprove(data.paypal?.approve_url);
        redirectingToPayPal = true;
        return;
      }

      const products = showProductSelect
        ? selectedServices
        : [serverType || lockedProduct || ""].filter(Boolean);
      const submitted: TrialResult[] = [];
      for (const product of products) {
        const res = await apiFetch("/api/pos/trials", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            product,
            company_name: companyName.trim(),
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            email: email.trim(),
            telephone: cellPhone.trim() || undefined,
            company_code: companyCode.trim().toLowerCase(),
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.success === false) {
          if (data.field_errors && typeof data.field_errors === "object") {
            setFieldErrors(data.field_errors as Record<string, string>);
          }
          throw new Error(data.error || "We could not submit your free trial right now.");
        }
        submitted.push(data.trial as TrialResult);
      }
      setTrials(submitted);
      setTrial(submitted[0] ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      // Keep button disabled while the browser navigates to PayPal
      if (!redirectingToPayPal) setLoading(false);
    }
  }

  if (order) {
    const provision = order.provision || {};
    const publicIp = provision.public_ip;
    const site = provision.cname_url || provision.cname;
    const paid =
      order.status === "paid" ||
      order.status === "paid_queued" ||
      order.status === "provisioned" ||
      order.status === "paid_provision_failed";

    return (
      <div className="deposit-success">
        <h3>
          {order.status === "provisioned"
            ? "Your server is ready"
            : paid
              ? "Payment received"
              : "Order update"}
        </h3>
        <p>
          Thanks, <strong>{order.first_name}</strong>. Your PayPal payment for the{" "}
          <strong>{order.product_label || "server"}</strong> deposit
          {order.email ? (
            <>
              {" "}
              (<strong>{order.email}</strong>)
            </>
          ) : null}{" "}
          is recorded.
        </p>
        {order.payment?.capture_status && (
          <p className="muted">
            PayPal status: <code>{order.payment.capture_status}</code>
          </p>
        )}
        {provision.error && (
          <p className="model-run-error" role="alert">
            Provisioning note: {provision.error}
          </p>
        )}
        {provision.message && <p>{provision.message}</p>}
        {publicIp && (
          <p>
            <strong>IP address:</strong>{" "}
            <a href={`http://${publicIp}`} target="_blank" rel="noreferrer">
              {publicIp}
            </a>
          </p>
        )}
        {site && (
          <p>
            <strong>Website address:</strong>{" "}
            {String(site).startsWith("http") ? (
              <a href={String(site)} target="_blank" rel="noreferrer">
                {site}
              </a>
            ) : (
              site
            )}
          </p>
        )}
        {provision.instance_id && (
          <p className="muted">
            Instance <code>{provision.instance_id}</code>
          </p>
        )}
        <p className="muted">
          Order ID <code>{order.id}</code>
        </p>
      </div>
    );
  }

  if (trial || trials.length > 0) {
    const list = trials.length > 0 ? trials : trial ? [trial] : [];
    const first = list[0];
    const labels = list
      .map((t) => t.product_label)
      .filter(Boolean)
      .join(", ");
    return (
      <div className="deposit-success">
        <h3>You are on the list</h3>
        <p>
          Thanks
          {first?.company_name ? (
            <>
              , <strong>{first.company_name}</strong>
            </>
          ) : null}
          . We will email <strong>{first?.email}</strong>
          {labels ? (
            <>
              {" "}
              about your <strong>{labels}</strong> trial
              {list.length > 1 ? "s" : ""}
            </>
          ) : null}{" "}
          when setup is ready.
        </p>
        {first?.message && <p>{first.message}</p>}
      </div>
    );
  }

  function labeled(text: string, required: boolean): ReactNode {
    return (
      <>
        {text}
        {required ? <ReqMark /> : null}
      </>
    );
  }

  return (
    <form className="deposit-form buy-server-form" onSubmit={onSubmit} noValidate>
      {showProductSelect ? (
        <div className="buy-server-row">
          <span id="buy-server-type-label" className="buy-server-service-label">
            {labeled("Service", true)}
          </span>
          <div className="buy-server-control">
            <fieldset
              className="buy-server-service-checks"
              aria-labelledby="buy-server-type-label"
              aria-invalid={Boolean(fieldErrors.server_type)}
              disabled={loading || serviceOptions.length === 0}
            >
              {serviceOptions.map((s) => (
                <label key={s.id} className="buy-server-service-check">
                  <input
                    type="checkbox"
                    name="service"
                    value={s.id}
                    checked={selectedServices.includes(s.id)}
                    onChange={() => toggleService(s.id)}
                  />
                  <span>{s.label}</span>
                </label>
              ))}
            </fieldset>
            <FieldError message={fieldErrors.server_type} />
            {selectedServices.length > 0 && (
              <p className="buy-server-product-note form-disclaimer form-disclaimer--under-select">
                {TRIAL_DISCLAIMER}
              </p>
            )}
          </div>
        </div>
      ) : (
        <p className="form-disclaimer form-disclaimer--under-select">
          {isServer ? SERVER_REFUND_DISCLAIMER : TRIAL_DISCLAIMER}
        </p>
      )}

      {isServer ? (
        <>
          <div className="buy-server-row">
            <label htmlFor="buy-first-name">{labeled("First name", true)}</label>
            <div className="buy-server-control">
              <input
                id="buy-first-name"
                type="text"
                required
                aria-required="true"
                aria-invalid={Boolean(fieldErrors.first_name)}
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  clearField("first_name");
                }}
                placeholder="Jane"
                autoComplete="given-name"
              />
              <FieldError message={fieldErrors.first_name} />
            </div>
          </div>
          <div className="buy-server-row">
            <label htmlFor="buy-last-name">{labeled("Last name", true)}</label>
            <div className="buy-server-control">
              <input
                id="buy-last-name"
                type="text"
                required
                aria-required="true"
                aria-invalid={Boolean(fieldErrors.last_name)}
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  clearField("last_name");
                }}
                placeholder="Smith"
                autoComplete="family-name"
              />
              <FieldError message={fieldErrors.last_name} />
            </div>
          </div>
          <div className="buy-server-row">
            <label htmlFor="buy-email">{labeled("Email address", true)}</label>
            <div className="buy-server-control">
              <input
                id="buy-email"
                type="email"
                required
                aria-required="true"
                aria-invalid={Boolean(fieldErrors.email)}
                value={email}
                onChange={(e) => {
                  const v = e.target.value;
                  setEmail(v);
                  setUsername((prev) => (prev ? prev : v));
                  clearField("email");
                }}
                placeholder="you@company.com"
                autoComplete="email"
              />
              <FieldError message={fieldErrors.email} />
            </div>
          </div>
          <div className="buy-server-row">
            <label htmlFor="buy-username">{labeled("Username", true)}</label>
            <div className="buy-server-control">
              <input
                id="buy-username"
                type="email"
                required
                aria-required="true"
                aria-invalid={Boolean(fieldErrors.username)}
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  clearField("username");
                }}
                placeholder="user@example.com"
              />
              <FieldError message={fieldErrors.username} />
            </div>
          </div>
          <div className="buy-server-row">
            <label htmlFor="buy-phone">Cell phone</label>
            <div className="buy-server-control">
              <input
                id="buy-phone"
                type="tel"
                aria-invalid={Boolean(fieldErrors.cell_phone)}
                value={cellPhone}
                onChange={(e) => {
                  setCellPhone(formatPhoneInput(e.target.value));
                  clearField("cell_phone");
                }}
                placeholder="(555) 123-4567"
                autoComplete="tel"
              />
              <FieldError message={fieldErrors.cell_phone} />
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="buy-server-row">
            <label htmlFor="buy-company">{labeled("Company", true)}</label>
            <div className="buy-server-control">
              <input
                id="buy-company"
                type="text"
                required
                aria-required="true"
                aria-invalid={Boolean(fieldErrors.company_name)}
                value={companyName}
                onChange={(e) => {
                  setCompanyName(e.target.value);
                  clearField("company_name");
                }}
                placeholder="Your company name"
              />
              <FieldError message={fieldErrors.company_name} />
            </div>
          </div>
          <div className="buy-server-row">
            <label htmlFor="buy-trial-first">{labeled("Your name", true)}</label>
            <div className="buy-server-control">
              <div className="buy-pos-name-pair">
                <input
                  id="buy-trial-first"
                  type="text"
                  required
                  aria-required="true"
                  aria-invalid={Boolean(fieldErrors.first_name)}
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    clearField("first_name");
                  }}
                  placeholder="First name"
                  autoComplete="given-name"
                />
                <input
                  id="buy-trial-last"
                  type="text"
                  required
                  aria-required="true"
                  aria-invalid={Boolean(fieldErrors.last_name)}
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    clearField("last_name");
                  }}
                  placeholder="Last name"
                  autoComplete="family-name"
                />
              </div>
              <FieldError message={fieldErrors.first_name || fieldErrors.last_name} />
            </div>
          </div>
          <div className="buy-server-row">
            <label htmlFor="buy-trial-email">{labeled("Email", true)}</label>
            <div className="buy-server-control">
              <input
                id="buy-trial-email"
                type="email"
                required
                aria-required="true"
                aria-invalid={Boolean(fieldErrors.email)}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  clearField("email");
                }}
                placeholder="you@company.com"
                autoComplete="email"
              />
              <FieldError message={fieldErrors.email} />
            </div>
          </div>
          <div className="buy-server-row">
            <label htmlFor="buy-trial-phone">Telephone</label>
            <div className="buy-server-control">
              <input
                id="buy-trial-phone"
                type="tel"
                aria-invalid={Boolean(fieldErrors.cell_phone)}
                value={cellPhone}
                onChange={(e) => {
                  setCellPhone(formatPhoneInput(e.target.value));
                  clearField("cell_phone");
                }}
                placeholder="111-222-3333"
                autoComplete="tel"
              />
              <FieldError message={fieldErrors.cell_phone} />
            </div>
          </div>
          <div className="buy-server-row">
            <label htmlFor="buy-company-code">{labeled("Company code", true)}</label>
            <div className="buy-server-control">
              <input
                id="buy-company-code"
                type="text"
                required
                aria-required="true"
                aria-invalid={Boolean(fieldErrors.company_code)}
                value={companyCode}
                onChange={(e) => {
                  setCompanyCode(e.target.value.toLowerCase());
                  clearField("company_code");
                }}
                placeholder="acme01"
              />
              <FieldError message={fieldErrors.company_code} />
              <p className="buy-server-product-note">
                4–20 characters, lowercase letters and numbers only.
              </p>
            </div>
          </div>
        </>
      )}

      {error && (
        <p className="model-run-error" role="alert">
          {error}
        </p>
      )}

      <div className="buy-server-actions">
        <button
          type="submit"
          className="btn btn-paypal"
          disabled={loading || (showProductSelect ? selectedServices.length === 0 : !serverType)}
        >
          {loading
            ? isServer
              ? "Redirecting to PayPal…"
              : "Submitting…"
            : isServer
              ? `Pay with PayPal · $${amount}`
              : "Signup for 15 day free trial"}
        </button>
      </div>
    </form>
  );
}
